import type { ReactNode } from "react";
import { Calculator } from "@untitledui/icons";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Popover as AriaPopover } from "react-aria-components";
import type { ExplainLine } from "../lib/commission";
import { cx } from "../utils/cx";
import { Money, type MoneyProps } from "./Money";

export interface ExplainAmountProps {
    /** The amount shown as the trigger (rial). */
    rial: bigint | string;
    /** Calculation lines, top to bottom. */
    lines: ExplainLine[];
    /** Popover heading. */
    title?: ReactNode;
    /** Optional footnote under the calculation (e.g. the rate-card version). */
    footnote?: ReactNode;
    /** Props forwarded to the trigger's Money. */
    moneyProps?: Omit<MoneyProps, "rial">;
    className?: string;
}

/**
 * Every money figure can be opened (spec §14.6.2): click or press Enter on the amount to see
 * how it was calculated. Popover placement is logical, so it opens toward the content in RTL.
 */
export const ExplainAmount = ({ rial, lines, title = "این مبلغ چطور حساب شد؟", footnote, moneyProps, className }: ExplainAmountProps) => (
    <AriaDialogTrigger>
        <AriaButton
            className={cx(
                "group inline-flex cursor-pointer items-baseline gap-1 rounded-md text-primary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
                className,
            )}
        >
            <span className="underline decoration-fg-quaternary decoration-dotted decoration-1 underline-offset-[6px] group-hover:decoration-fg-brand-primary">
                <Money rial={rial} {...moneyProps} />
            </span>
            <span className="sr-only">نمایش محاسبه</span>
        </AriaButton>
        <AriaPopover
            placement="bottom start"
            offset={8}
            className={({ isEntering, isExiting }) =>
                cx(
                    "w-[min(22rem,calc(100vw-2rem))] origin-(--trigger-anchor-point) rounded-xl bg-primary shadow-lg ring-1 ring-secondary_alt outline-hidden will-change-transform",
                    isEntering && "duration-150 ease-out animate-in fade-in placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                    isExiting && "duration-100 ease-in animate-out fade-out placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                )
            }
        >
            <AriaDialog className="flex flex-col gap-3 p-4 outline-hidden" aria-label={typeof title === "string" ? title : "محاسبهٔ مبلغ"}>
                <div className="flex items-center gap-2">
                    <Calculator className="size-4 text-fg-brand-primary" aria-hidden="true" />
                    <p className="text-sm font-semibold text-primary">{title}</p>
                </div>
                <dl className="flex flex-col gap-1.5">
                    {lines.map((line, i) => (
                        <div
                            key={i}
                            className={cx(
                                "flex items-baseline justify-between gap-3 text-sm",
                                line.emphasis && "mt-1 border-t border-secondary pt-2.5 font-semibold text-primary",
                            )}
                        >
                            <dt className={cx("flex items-baseline gap-2", line.emphasis ? "text-primary" : "text-tertiary")}>
                                <span className="inline-block w-3 text-center font-semibold text-quaternary" aria-hidden="true">
                                    {line.op ?? ""}
                                </span>
                                {line.label}
                            </dt>
                            <dd className={cx("shrink-0 digits-tabular", line.emphasis ? "text-brand-secondary" : "text-secondary")}>
                                <bdi dir="ltr">{line.value.replace(/ تومان$/, "")}</bdi>
                                {line.value.endsWith(" تومان") && <span className="ms-1 text-xs text-tertiary">تومان</span>}
                            </dd>
                        </div>
                    ))}
                </dl>
                {footnote && <p className="text-xs text-tertiary">{footnote}</p>}
            </AriaDialog>
        </AriaPopover>
    </AriaDialogTrigger>
);
